import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, BookOpen, GraduationCap, Languages, MessageSquare, Mic, Sparkles, ChevronRight } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import PullToRefresh from '../../components/PullToRefresh'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { useEnglishDueCount } from '../../hooks/useEnglishReview'
import { useEnglishCourseProgress } from '../../hooks/useEnglishCourses'
import { cn } from '../../lib/utils'

// Owner-only English-learning dashboard. Phase 1 PR A scaffolds the
// route and renders the four-card landing layout that subsequent PRs
// fill in with live data:
//   - Today's reviews   (PR C: SRS engine)
//   - Vocabulary        (PR B: capture flow)
//   - Course progress   (PR D: course tracker)
//   - Recent activity   (PR C/D: review + lecture timeline)
// For now each card is a tappable placeholder pointing at the
// dedicated subroute. The intent is that the dashboard reflects the
// user's English-study state at a glance and the subroutes are where
// you actually do the work.

function DashboardTile({ icon: Icon, title, subtitle, onClick, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-start"
      dir="auto"
    >
      <Card className="hover:border-app-primary-500/40 transition-colors">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-app-primary-500/10 text-app-primary-500 flex-shrink-0">
              <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground" dir="auto">{title}</p>
              <p className="text-xs text-muted-foreground mt-0.5" dir="auto">{subtitle}</p>
            </div>
            {count != null && (
              <span className="text-lg font-bold text-app-primary-500 font-mono tabular-nums" dir="ltr">
                {count}
              </span>
            )}
            <ChevronRight className={cn(
              'w-4 h-4 text-muted-foreground flex-shrink-0',
              'rtl:-scale-x-100',
            )} />
          </div>
        </CardContent>
      </Card>
    </button>
  )
}

export function EnglishPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()

  // Owner gate. The route is mounted globally but only the owner can
  // see content; everyone else gets a quiet redirect to home.
  useEffect(() => {
    if (authLoading) return
    if (!user || !hasOwnerAccess(user)) navigate('/', { replace: true })
  }, [user, authLoading, navigate])

  const goBack = () => navigate('/')
  const handleRefresh = () => Promise.resolve()
  const dueCount = useEnglishDueCount()
  const courseProgress = useEnglishCourseProgress()

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-3 content-fade pb-8">
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            onClick={goBack}
            aria-label={t('settings.back')}
            className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
          >
            <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-foreground flex items-center gap-1.5" dir="auto">
              <Languages className="w-4 h-4 text-app-primary-500" />
              {t('english.title')}
            </h1>
            <p className="text-xs text-muted-foreground" dir="auto">{t('english.subtitle')}</p>
          </div>
        </div>

        {/* Hero hint until the user has data. Replaced by a real
            streak / stats strip in PR C/D. */}
        <Card className="border-app-primary-500/30 bg-app-primary-500/5">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-app-primary-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-foreground leading-relaxed" dir="auto">
                {t('english.welcomeHint')}
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-2">
          <DashboardTile
            icon={Sparkles}
            title={t('english.dashboard.reviewTitle')}
            subtitle={dueCount.count > 0
              ? t('english.dashboard.reviewDue', { count: dueCount.count })
              : t('english.dashboard.reviewSubtitle')}
            onClick={() => navigate('/english/review')}
            count={dueCount.loading ? null : (dueCount.count > 0 ? dueCount.count : null)}
          />
          <DashboardTile
            icon={BookOpen}
            title={t('english.dashboard.vocabTitle')}
            subtitle={t('english.dashboard.vocabSubtitle')}
            onClick={() => navigate('/english/vocab')}
          />
          <DashboardTile
            icon={GraduationCap}
            title={t('english.dashboard.courseTitle')}
            subtitle={courseProgress.course
              ? t('english.dashboard.courseSubtitleActive', {
                  title: courseProgress.course.title,
                  done: courseProgress.course.completed_lectures || 0,
                  total: courseProgress.course.total_lectures || 0,
                })
              : t('english.dashboard.courseSubtitle')}
            onClick={() => navigate('/english/course')}
            count={!courseProgress.loading && courseProgress.count > 1 ? courseProgress.count : null}
          />
          <DashboardTile
            icon={MessageSquare}
            title={t('english.dashboard.tutorTitle')}
            subtitle={t('english.dashboard.tutorSubtitle')}
            onClick={() => navigate('/english/tutor')}
          />
          <DashboardTile
            icon={Mic}
            title={t('english.dashboard.speakTitle')}
            subtitle={t('english.dashboard.speakSubtitle')}
            onClick={() => navigate('/english/speak')}
          />
        </div>
      </div>
    </PullToRefresh>
  )
}
